import BaseApiService from './BaseApiService'

/** The team block library (/v1/blocks). Any editor may write; viewers may list, get, see usage and preview. */
export interface BlockVariable {
  name: string
  kind: string
}

export interface ContentBlockSummary {
  contentBlockId: string
  name: string
  description: string | null
  category: string | null
  tags: string[]
  variables: BlockVariable[] | null
  usageCount: number
  lastUsedAt: string | null
  archived: boolean
  createdBy: string
  createdByEmail: string | null
  createdAt: string
  updatedAt: string
}

export interface BlockDetails {
  name: string
  description?: string
  category?: string
  tags?: string[]
}

/** contentJson: a one-section CanonicalDocument whose sections[0].body is the block. */
export interface SaveBlockInput extends BlockDetails {
  contentJson: string
  sourceTemplateId?: string
  sourceTemplateVersionId?: string
}

export interface BlockUsage {
  templateId: string
  templateName: string | null
  insertedAt: string
  insertedByEmail: string | null
}

export interface ListBlocksFilter {
  search?: string
  category?: string
  includeArchived?: boolean
}

class BlocksApiService extends BaseApiService {
  list(filter: ListBlocksFilter = {}): Promise<ContentBlockSummary[]> {
    return this.post('/v1/blocks/list', filter)
  }

  getBlock(contentBlockId: string): Promise<{ block: ContentBlockSummary; contentJson: string }> {
    return this.post('/v1/blocks/get', { contentBlockId })
  }

  create(input: SaveBlockInput): Promise<{ contentBlockId: string }> {
    return this.post('/v1/blocks/create', input)
  }

  update(contentBlockId: string, input: SaveBlockInput): Promise<{ contentBlockId: string }> {
    return this.post('/v1/blocks/update', { contentBlockId, ...input })
  }

  archive(contentBlockId: string, archived: boolean): Promise<{ archived: boolean }> {
    return this.post('/v1/blocks/archive', { contentBlockId, archived })
  }

  delete(contentBlockId: string): Promise<{ deleted: boolean }> {
    return this.post('/v1/blocks/delete', { contentBlockId })
  }

  /** A copy of the block for templateId, its images staged for that template. Records usage. */
  insert(contentBlockId: string, templateId: string): Promise<{ contentJson: string; variables: BlockVariable[] | null }> {
    return this.post('/v1/blocks/insert', { contentBlockId, templateId })
  }

  usage(contentBlockId: string): Promise<BlockUsage[]> {
    return this.post('/v1/blocks/usage', { contentBlockId })
  }

  /** The block rendered alone, fields shown by name. Not billed. */
  preview(contentBlockId: string, signal?: AbortSignal): Promise<Blob> {
    return this.postBlob('/v1/blocks/preview', { contentBlockId }, signal)
  }
}

export const blocksApiService = new BlocksApiService()
